import { Reorder } from "framer-motion";
import React, { useEffect, useState } from 'react';
import Metrics from './Metrics';
import api from '../../api/api';
import { useNavigate, useLocation } from 'react-router-dom';
import { convertSingleUnit, convertSingleValue, convertSingleValueOnly, currencyToSymbol, formatFiscalDateFromString } from '../../utils/commonUtils';
import SaveSettingButton from "./SaveSettingButton";
import { Calendar } from "lucide-react";
import Loading from "../../Loading";
import { useDashboardContext } from '../../../context/DashboardContext';


const MainMetrics = ({ companyInfo, accountingStandard, financialData, stockPriceData, metricsList, setLoaded, stockCurrency }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [currentMetricsSettings, setCurrentMetricsSettings] = useState([]);
    const [metrics, setMetrics] = useState(null);
    const stockCurrencyUnit = currencyToSymbol(stockCurrency)
    const latestFinancialData = financialData[financialData.length - 1];
    let prevFinancialData = financialData.length > 0 ? financialData[financialData.length - 2] : null;
    const { state: dashboardState } = useDashboardContext();
    
    const [dividendData, setDividendData] = useState(null);

    const calcDiff = (latest, prev, col) => {
        return (latest[col] - prev[col]).toFixed(2);
    }
        
    const calcPctChange = (latest, prev, col) => {
        if (!latest) return '-';
        if (!prev[col]) return '-';
        if (prev[col] < 0) return '前期マイナス';
        return ((calcDiff(latest, prev, col) / prev[col]) * 100).toFixed(1);
    };

    const calcLatestInfo = (data, column) => {
        if (!data) {
            return {
                date: null,
                value: null,
                prevDate: null,
                diff: null,
                pctChange: null,
            }
        }

        const latestIndex = stockPriceData.length - 1
        if (data.length > 1) {
            return {
                date: stockPriceData[latestIndex].date,
                value: stockPriceData[latestIndex][column],
                prevDate: stockPriceData[latestIndex].date,
                diff: stockPriceData[latestIndex][column] - stockPriceData[latestIndex - 1][column],
                pctChange: stockPriceData[latestIndex - 1][column]  ? (stockPriceData[latestIndex][column] - stockPriceData[latestIndex - 1][column]) / stockPriceData[latestIndex - 1][column] * 100 : null,
            }
        } else if (data.length === 1) {
            return {
                date: stockPriceData[latestIndex].date,
                value: stockPriceData[latestIndex][column],
                prevDate: null,
                diff: null,
                pctChange: null,
            }
        } else {
            return {
                date: null,
                value: null,
                prevDate: null,
                diff: null,
                pctChange: null,
            }
        }
        
    }

    useEffect(() => {
        const fetchMetricsSetting = async () => {
            try {
                const response = await api.get(`${process.env.REACT_APP_API_URL}/accounts/api/metrics-settings/`);
                setCurrentMetricsSettings(response.data);
            } catch (error) {
                if (error.response && [401, 403].includes(error.response.status)) {
                    navigate('/login', { state: { from: location.pathname } });
                } else {
                    console.error('Error fetching metrics settings:', error);
                }
            }
        };
        setLoaded((prev) => !prev); // 無理やり
        fetchMetricsSetting();
    }, []);

    const handleReorder = (newOrder) => {
        // newOrderのIDを基に、currentMetricsSettingsを並べ替える
        const reOrderedMetricsSettings = newOrder.map((o, index) => {
            const originalMetric = currentMetricsSettings.find(item => item.metrics.metric === o.metric);
            return { metrics: originalMetric.metrics, order: index + 1 };
        });
        
        setCurrentMetricsSettings(reOrderedMetricsSettings);
    };

    const convertDate = date => {
        const [year, month, day] = date.split('-');
        return `${parseInt(month, 10)}月${parseInt(day, 10)}日`;
    };

    const makeMetricData = (metric) => {
        const id = metric?.metric || metric?.id;
        const commonData = {
            id,
            metric: id,
            icon: <Calendar className="w-5 h-5 text-primary-500 dark:text-primary-400" />,
        };

        if (metric?.metric === 'stock_price') {
            const latestStockPrice = calcLatestInfo(stockPriceData, 'close')
            const date = latestStockPrice?.date ? convertDate(latestStockPrice?.date) : '';
            return {
                ...commonData,
                title: "株価 (" + date + "終値)",
                value: latestStockPrice?.value || "-",
                unit: stockCurrencyUnit,
                trend: latestStockPrice?.pctChange?.toFixed(1),
                trendUnit: '%',
                vs: '前日比',
            };
        } else if (metric?.metric === 'market_value') {
            const latestMarketValue = calcLatestInfo(stockPriceData, metric.metric)
            return {
                ...commonData,
                title: metric.metric_name,
                value: convertSingleValueOnly(latestMarketValue.value),
                unit: convertSingleUnit(latestMarketValue.value) + reportedCurrencyUnit,
                trend: latestMarketValue?.pctChange?.toFixed(1),
                trendUnit: '%',
                vs: '前日比',
            };
        } else if (['per', 'pbr', 'psr', 'dividend_yield'].includes(metric?.metric)) {
            if (['per', 'pbr', 'psr'].includes(metric?.metric) && !['USD', 'JPY'].includes(latestFinancialData?.reported_currency)) {
                return {
                    ...commonData,
                    title: metric.metric_name,
                    value: '-',
                    unit: '',
                    trend: null,
                    trendUnit: '',
                    vs: '-',
                };
            }

            const latestValue = calcLatestInfo(stockPriceData, metric.metric)
            return {
                ...commonData,
                title: metric.metric_name,
                value: latestValue ? convertSingleValueOnly(latestValue.value) : '',
                unit: convertSingleUnit(latestValue.value) + metric.unit,
                trend: latestValue?.diff?.toFixed(2),
                trendUnit: metric.unit,
                vs: '前日比',
            };
        } else if (['volume'].includes(metric?.metric)) {
            const latestValue = calcLatestInfo(stockPriceData, metric.metric)
            return {
                ...commonData,
                title: metric.metric_name,
                value: latestValue ? convertSingleValueOnly(latestValue.value) : '',
                unit: convertSingleUnit(latestValue.value) + metric.unit,
                trend: latestValue?.pctChange?.toFixed(1),
                trendUnit: '%',
                vs: '前日比',
            };
        } else if (metric?.category === 'PL' || metric?.category === 'BS' || metric?.category === 'CF') {
            let value;
            let unit;
            let trend;
            if (!latestFinancialData) {
                value = null;
                unit = null
                trend = null;
            } else {
                value = latestFinancialData ? convertSingleValueOnly(latestFinancialData[metric.metric]) : '';
                unit = convertSingleUnit(latestFinancialData[metric.metric]) + reportedCurrencyUnit
                trend = calcPctChange(latestFinancialData, prevFinancialData, metric.metric)
            }
            return {
                ...commonData,
                title: metric.metric_name,
                value: value,
                unit: unit,
                trend: trend,
                trendUnit: '%',
                vs: '前年比',
            };
        } else if (metric.category === '指標') {
            let value;
            let unit;
            let trend;
            if (!latestFinancialData) {
                value = null;
                unit = null
                trend = null;
            } else {
                value = latestFinancialData ? convertSingleValueOnly(latestFinancialData[metric.metric]): ''
                unit = convertSingleUnit(latestFinancialData[metric.metric]) + metric.unit
                trend = calcDiff(latestFinancialData, prevFinancialData, metric.metric)
            }
            
            return {
                ...commonData,
                title: metric.metric_name,
                value: value,
                unit: unit,
                trend: trend,
                trendUnit: metric.unit,
                vs: '前年比',
            };
        } else if (metric.metric === 'industry') {
            return {
                ...commonData,
                title: metric.metric_name,
                value: companyInfo.industry.industry_name,
                unit: '',
                trend: '-',
                trendUnit: '',
                vs: '',
            };
        } else if (metric.metric === 'number_of_employees') {
            return {
                ...commonData,
                title: metric.metric_name,
                value: companyInfo.fulltimeemployees?.toLocaleString(),
                unit: '人',
                trend: '-',
                trendUnit: '',
                vs: '',
            };
        } else if (metric.metric === 'accounting_standard') {
            return {
                ...commonData,
                title: metric.metric_name,
                value: accountingStandard,
                unit: '',
                trend: '-',
                trendUnit: '',
                vs: '',
            };
        } else if (['establishment_date', 'ipodate'].includes(metric.metric)) {
            return {
                ...commonData,
                title: metric.metric_name,
                value: formatFiscalDateFromString(companyInfo[metric.metric], 'YYYY年m月d日') ,
                unit: '',
                trend: '-',
                trendUnit: '',
                vs: '',
            };
        } else if (metric.metric === 'dividend') {
            let latestDividend;
            let fiscalYear;
            if (dividendData && dividendData.length > 0) {
                latestDividend = dividendData[dividendData.length - 1].adjdividend;
                fiscalYear = dividendData[dividendData.length - 1].fiscal_year;
            } else {
                latestDividend = 0;
                fiscalYear = ''
            }
    
            let prevDividend;
            let diff;
            if (dividendData && dividendData.length > 1) {
                prevDividend = dividendData[0].adjdividend;
                diff = (latestDividend - prevDividend).toFixed(2);
            } else {
                prevDividend = 0;
                diff = 0;
            }
    
            const unit = financialData[financialData.length - 1]?.reported_currency;
    
            return {
                ...commonData,
                title: metric.metric_name,
                value: latestDividend.toFixed(2),
                unit: `${currencyToSymbol(unit)}${fiscalYear ? ' (' + fiscalYear + '年度)' : ''}`,
                trend: diff,
                trendUnit: currencyToSymbol(unit),
                vs: '前回比',
            };
        } else if (metric.metric === 'payout_ratio') {
            let latestRatio;
            let fiscalYear;
            if (dividendData && dividendData.length > 0) {
                latestRatio = dividendData[dividendData.length - 1].payout_ratio;
                fiscalYear = dividendData[dividendData.length - 1].fiscal_year;
            } else {
                latestRatio = 0;
                fiscalYear = ''
            }
    
            let prevRatio;
            let diff;
            if (dividendData && dividendData.length > 1) {
                prevRatio = dividendData[0].adjdividend;
                diff = (latestRatio - prevRatio).toFixed(2);
            } else {
                prevRatio = 0;
                diff = 0;
            }
    
            return {
                ...commonData,
                title: metric.metric_name,
                value: latestRatio,
                unit: metric.unit,
                trend: diff,
                trendUnit: metric.unit,
                vs: '前回比',
            };
        }
        
        return null;
    };

    const onSaveMainMetricsClick = async () => {
        const metricsData = currentMetricsSettings.reduce((acc, item, index) => {
            acc[index + 1] = item.metrics.metric;
            return acc;
        }, {});
        await api.post(`${process.env.REACT_APP_API_URL}/accounts/api/metrics-settings/`, { metrics: metricsData });
    };

    const onMetricChange = (currentMetric, category, newMetric) => {
        // `metricsList`から`category`と`newMetric`に一致する新しいデータを取得
        const newMetricData = metricsList.find(item => item.category === category)
            ?.metrics.find(metric => metric.metric === newMetric);
        newMetricData['category'] = category;
        if (!newMetricData) {
            console.warn('New metric data not found for:', newMetric);
            return;
        }
    
        // `currentMetricsSettings`を更新
        const updatedMetricsSettings = currentMetricsSettings.map(setting => {
            // `currentMetric`と一致する場合に`newMetricData`で置き換え
            if (setting.metrics.metric === currentMetric) {
                return { ...setting, metrics: newMetricData, order: setting.order };
            }
            return setting;
        });
        setCurrentMetricsSettings(updatedMetricsSettings);
    };
    

    useEffect(() => {
        if (currentMetricsSettings) {
            if (currentMetricsSettings.some(item => item.metrics.metric === 'dividend')) {
                getLatestDividendData();
            }
        }
    }, [currentMetricsSettings])

    useEffect(() => {
        if (currentMetricsSettings) {
            const metricsData = currentMetricsSettings.map(metric => makeMetricData(metric.metrics)).filter(Boolean);
            setMetrics(metricsData);
        }
    }, [currentMetricsSettings, dividendData, stockPriceData, financialData]);

    const getLatestDividendData = async () => {
        try {
            const response = await api.get(`${process.env.REACT_APP_API_URL}/chat/api/dividend-time-series/${dashboardState.secCode}/`, {
                params: {
                    exclude_forecast: true,
                    total_only: true,
                    limit: 2
                }
            });
            setDividendData(response.data); // 非同期処理の結果を返す
        } catch (error) {
            if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                navigate('/login', { state: { from: location.pathname } });
            } else {
                console.error('Error fetching dividend time series data:', error);
            }
        }
    };

    if (!financialData || !stockPriceData || !metricsList || !metrics) {
        return <Loading />
    }
    
    const reportedCurrency = financialData[0]?.reported_currency;
    const reportedCurrencyUnit = currencyToSymbol(reportedCurrency)
    
    return (
        <div className="dashboard-row" style={{marginTop: 0}} >
            <div className="dashboard-title-container flex">
                <div className="dashboard text-xl">主要指標</div>
                <SaveSettingButton onSaveClick={onSaveMainMetricsClick} />
            </div>
            <div className="dashboard-metrics grid w-full">
                <Reorder.Group axis="x" values={metrics} onReorder={handleReorder} className="dashboard-metrics grid w-full">
                    {metrics.map((metric, index) => (
                        <Reorder.Item key={metric.id} value={metric} className="metric-box-small cursor-move ">
                            <Metrics {...metric} metricsList={metricsList} onMetricChange={onMetricChange} existingMetrics={metrics.map((m) => m.id)} />
                        </Reorder.Item>
                    ))}
                </Reorder.Group>
            </div>
        </div>
    );
};

export default MainMetrics;
